import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { useOrigins, useRegions, useProducers } from "@/hooks/useReferenceData";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { useAppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

interface ProducerManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProducerManagementDialog({ open, onOpenChange }: ProducerManagementDialogProps) {
  useAppLocale();
  const producers = useProducers();
  const origins = useOrigins();
  const regions = useRegions();
  const createProducer = useMutation(api.products.createProducer);
  const updateProducer = useMutation(api.products.updateProducer);
  const deleteProducer = useMutation(api.products.deleteProducer);

  const [search, setSearch] = React.useState("");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [originId, setOriginId] = React.useState("");
  const [regionId, setRegionId] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const originMap = React.useMemo(() => {
    const map = new Map<string, string>();
    origins?.forEach((o: any) => map.set(o._id, o.name));
    return map;
  }, [origins]);

  const regionMap = React.useMemo(() => {
    const map = new Map<string, string>();
    regions?.forEach((r: any) => map.set(r._id, r.name));
    return map;
  }, [regions]);

  const filteredRegions = React.useMemo(() => {
    if (!regions) return [];
    if (!originId) return regions;
    return regions.filter((r: any) => r.originId === originId);
  }, [regions, originId]);

  const originOptions = React.useMemo(
    () => (origins ?? []).map((origin: any) => ({ value: origin._id, label: origin.name })),
    [origins],
  );

  const regionOptions = React.useMemo(
    () => filteredRegions.map((region: any) => ({
      value: region._id,
      label: region.name,
      sublabel: region.originId ? originMap.get(region.originId) : undefined,
      searchText: [region.name, region.originId ? originMap.get(region.originId) : undefined]
        .filter(Boolean)
        .join(" "),
    })),
    [filteredRegions, originMap],
  );

  const filtered = React.useMemo(() => {
    if (!producers) return [];
    if (!search) return producers;
    const q = search.toLowerCase();
    return producers.filter((p: any) => {
      const pName = p.name?.toLowerCase() ?? "";
      const oName = originMap.get(p.originId)?.toLowerCase() ?? "";
      const rName = regionMap.get(p.regionId)?.toLowerCase() ?? "";
      return pName.includes(q) || oName.includes(q) || rName.includes(q);
    });
  }, [producers, search, originMap, regionMap]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setOriginId("");
    setRegionId("");
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(m.name_required_simple());
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateProducer({
          id: editingId as any,
          name: name.trim(),
          description: description || undefined,
          originId: originId || undefined,
          regionId: regionId || undefined,
        });
        toast.success(m.producer_updated());
      } else {
        await createProducer({
          name: name.trim(),
          description: description || undefined,
          originId: originId || undefined,
          regionId: regionId || undefined,
        });
        toast.success(m.producer_created());
      }
      resetForm();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to save producer", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (producer: any) => {
    setEditingId(producer._id);
    setName(producer.name);
    setDescription(producer.description ?? "");
    setOriginId(producer.originId ?? "");
    setRegionId(producer.regionId ?? "");
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProducer({ id: id as any });
      toast.success(m.producer_deleted());
      if (editingId === id) resetForm();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Failed to delete producer", {
          unauthorizedMessage:
            m.inventory_products_unauthorized(),
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{m.manage_producers()}</DialogTitle>
          <DialogDescription>{m.manage_producers_description()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-3 rounded-md border p-3">
            <div className="grid gap-2">
              <Label>{editingId ? m.edit_producer() : m.add_producer()}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={m.producer_name()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.description()}</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={m.optional_description()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.origin()}</Label>
              <ComboboxSelect
                options={originOptions}
                value={originId}
                onValueChange={(value) => {
                  setOriginId(value);
                  setRegionId("");
                }}
                placeholder={m.select_origin()}
                searchPlaceholder="Search origins..."
                emptyText="No origins found."
                loading={origins === undefined}
              />
            </div>
            <div className="grid gap-2">
              <Label>{m.region()}</Label>
              <ComboboxSelect
                options={regionOptions}
                value={regionId}
                onValueChange={setRegionId}
                placeholder={m.select_region()}
                searchPlaceholder="Search regions..."
                emptyText="No regions found."
                loading={regions === undefined}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
                {saving ? m.saving() : editingId ? m.update() : m.add()}
              </Button>
              {editingId && (
                <Button size="sm" variant="outline" onClick={resetForm}>{m.cancel()}</Button>
              )}
            </div>
          </div>

          <Input placeholder={m.search_producers()} value={search} onChange={(e) => setSearch(e.target.value)} />

          <div className="max-h-[300px] space-y-1 overflow-y-auto">
            {filtered.map((producer: any) => (
              <div
                key={producer._id}
                className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
              >
                <div className="flex items-center gap-2">
                  <div>
                    <p className="text-sm font-medium">{producer.name}</p>
                    {producer.description && (
                      <p className="text-xs text-muted-foreground">{producer.description}</p>
                    )}
                  </div>
                  {producer.originId && originMap.has(producer.originId) && (
                    <Badge variant="outline" className="text-xs">{originMap.get(producer.originId)}</Badge>
                  )}
                  {producer.regionId && regionMap.has(producer.regionId) && (
                    <Badge variant="secondary" className="text-xs">{regionMap.get(producer.regionId)}</Badge>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="icon-xs" variant="ghost" onClick={() => handleEdit(producer)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon-xs" variant="ghost" aria-label={`Delete ${producer.name}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete producer?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove <span className="font-medium">{producer.name}</span> from the catalog. Products referencing this producer will need to be reassigned. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          variant="destructive"
                          onClick={() => void handleDelete(producer._id)}
                        >
                          Delete producer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">{m.no_producers_found()}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
